import mixinTable from '@/components/mixin/mixinTable'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinPermissions from '@/components/mixin/mixinPermissions'
import mixinBackup from '@/components/mixin/mixinBackup'

// Server-side list for the Backup restore tabs. A component using it defines:
//   fetchRows({ filter, pagination, sort }) → store action promise
//   defaultSortField (string)
// Filter-driven loads are debounced (300 ms) and stale responses are dropped.
export default {
  mixins: [mixinTable, mixinNotifications, mixinPermissions, mixinBackup],
  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    rows: [],
    loadError: null,
    loadedOnce: false,
    requestSeq: 0,
    refreshTimer: null,
    // Multi-select for bulk restore, kept across pages: backupId → row
    picked: {},
  }),
  watch: {
    filters: {
      deep: true,
      handler() {
        this.pagination.page = 1
        this.picked = {}
        this.scheduleRefresh()
      },
    },
  },
  created() {
    this.pagination = { page: 1, total: 0, size: 25 }
    this.sort = { field: this.defaultSortField || null, direction: 'DESC' }
  },
  mounted() {
    this.refresh()
  },
  beforeDestroy() {
    clearTimeout(this.refreshTimer)
  },
  computed: {
    pickedRows() {
      return Object.values(this.picked)
    },
    pickedCount() {
      return this.pickedRows.length
    },
    selectableRows() {
      return this.rows.filter(r => this.isSelectable(r))
    },
    allPagePicked() {
      return this.selectableRows.length > 0 && this.selectableRows.every(r => this.picked[r.backupId])
    },
    somePagePicked() {
      return !this.allPagePicked && this.selectableRows.some(r => this.picked[r.backupId])
    },
  },
  methods: {
    getId(row) {
      return row && row.backupId
    },
    scheduleRefresh() {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = setTimeout(() => {
        this.refreshTimer = null
        this.refresh()
      }, 300)
    },
    async refresh() {
      clearTimeout(this.refreshTimer)
      const requestId = ++this.requestSeq
      const token = this.setLoading()
      const sort = { ...this.sort }
      if (!sort.field) sort.field = this.defaultSortField || null
      let result = null
      try {
        result = await this.fetchRows({
          filter: this.filters,
          pagination: { page: this.pagination.page, size: this.pagination.size },
          sort,
        })
      } catch (err) {
        if (requestId !== this.requestSeq) return
        this.loadError = err || {}
        this.rows = []
        this.setLoaded(token)
        return
      }
      if (requestId !== this.requestSeq) {
        this.setLoaded(token)
        return
      }
      this.loadError = null
      this.loadedOnce = true
      this.rows = (result && result.data) || []
      this.pagination = { ...this.pagination, ...(result.pagination || {}) }
      if (result.sort && result.sort.field) {
        this.setSort(result.sort.field, String(result.sort.direction || 'DESC').toUpperCase())
      }
      this.setLoaded(token)
      this.$emit('total', this.pagination.total)
    },
    onPaginationPage(page) {
      this.pagination.page = page
      this.refresh()
    },
    onPaginationSize(size) {
      this.pagination = { ...this.pagination, size, page: 1 }
      this.refresh()
    },
    onSortChanged(ctx) {
      this.setSort(ctx.sortBy, ctx.sortDesc ? 'DESC' : 'ASC')
      this.pagination.page = 1
      this.refresh()
    },
    // ---- selection ----
    isSelectable() {
      return false
    },
    isPicked(row) {
      return !!this.picked[row.backupId]
    },
    togglePick(row, value) {
      if (!this.isSelectable(row)) return
      const next = { ...this.picked }
      if (value) next[row.backupId] = row
      else delete next[row.backupId]
      this.picked = next
    },
    togglePagePick(value) {
      const next = { ...this.picked }
      this.selectableRows.forEach(r => {
        if (value) next[r.backupId] = r
        else delete next[r.backupId]
      })
      this.picked = next
    },
    clearPicked() {
      this.picked = {}
    },
    errorText() {
      return this.getErrorDescription(this.loadError)
    },
  },
}
